import axios from 'axios';
import { getToken } from '@/scripts/token.js';
import constants from '@/constants';

export const DISPUTES_PAGE_SIZE = 50;
import store from '@/store/index.js';
import { axiosRequest, METHOD } from '@/api/default.js';

export async function getFilesAxios() {
    const token = await getToken();
    if (token === null) {
        throw 'Authorization token was not detected';
    }

    const assetTypes = await calculateAssetType();

    const params = await getParams(store.getters.CURRENT_ASSET_TYPE == null);
    const paramsList = [];

    for (let index = 0; index < assetTypes.length; index++) {
        params['asset_type_id'] = assetTypes[index];
        paramsList.push(
            structuredClone({
                method: METHOD.GET,
                url: `${process.env.VUE_APP_BACKEND_URL}${constants.URL.FILE_GET}`,
                params: params,
                paramsSerializer: {
                    indexes: null,
                },
                headers: {
                    Authorization: token,
                    'Cache-Control': 'no-cache',
                },
            })
        );
    }

    return paramsList;
}

export async function getFiles() {
    try {
        const axiosPromise = await getFilesAxios();
        const files = {};

        await Promise.all(axiosPromise.map((params) => axios(params))).then(
            axios.spread((...allData) => {
                allData.forEach((data) => {
                    files[data.config.params.asset_type_id] = data.data;
                });
            })
        );

        return files;
    } catch (error) {
        return {};
    }
}

async function calculateAssetType() {
    const assetTypes = [];

    const assetType = store.getters.CURRENT_ASSET_TYPE;
    if (assetType != null && assetType !== 'all') {
        assetTypes.push(assetType);
    } else {
        const allAssetTypes = await store.getters.ASSET_TYPES;
        allAssetTypes.forEach((assetTypeValue, assetTypeIndex) => {
            assetTypes.push(assetTypeValue.id);
        });
    }

    return assetTypes;
}

export async function saveFiles(files, assetTypeID) {
    let data = [];

    if (assetTypeID === (await store.getters.ASSET_TYPE_IMAGES)) {
        data = await parseImageToSave(files);
    }

    if (data.length === 0) return [];

    const response = await axiosRequest({
        url: constants.URL.FILE_UPDATE,
        method: METHOD.PATCH,
        requestName: 'UPDATE FILE',
        data: data,
        params: {
            asset_type_id: assetTypeID,
        },
    });

    return response == null ? [] : response.files;
}

async function parseImageToSave(files) {
    if (files.length == null) files = [files];
    let saveList = [];

    files.forEach((fileValue, fileIndex) => {
        if (fileValue.isEdit === true && fileValue.isActive === true) {
            saveList.push({
                id: fileValue.id,
                additional_tags: fileValue.additionalTags,
                filter_items_id: fileValue.selectFilter,
                tags_id: fileValue.tags,
                is_hq: fileValue.isHq,
            });
        }
    });

    return saveList;
}

async function getParams(isOneAssetType = true) {
    let params = {};

    // File ID
    const fileID = store.getters.CURRENT_FILE_ID;
    if (fileID != null) params['id'] = fileID;

    // Provider
    const providerID = store.getters.CURRENT_PROVIDER;
    if (providerID != null && providerID !== 'all')
        params['provider_id'] = providerID;

    // Games
    const gameID = store.getters.CURRENT_GAME;
    if (gameID != null && gameID !== 'all') params['game_name_id'] = gameID;

    // Status
    const fileStatus = store.getters.CURRENT_FILE_STATUS;
    if (fileStatus != null && fileStatus !== 'all') {
        switch (fileStatus) {
            case 'active':
                params['is_active'] = true;
                break;
            case 'disable':
                params['is_active'] = false;
                break;
            case 'release':
                params['is_active'] = true;
                params['is_released'] = true;
                break;
            case 'unrelease':
                params['is_active'] = true;
                params['is_released'] = false;
                break;
        }
    }

    // Additional Status
    const additionalStatus = store.getters.CURRENT_ADDITIONAL_STATUS;
    if (additionalStatus != null && additionalStatus !== 'all')
        params['additional_status'] = additionalStatus;

    // Disputed asset type (GUP uploads page). Before the early return below,
    // which is exactly the state that page opens in.
    if (store.getters.CURRENT_DISPUTED === true) {
        params['disputed'] = true;
        // 1 293 disputes on 15.09 tripped the 700-row listing cap and the page
        // read "none". A server page has no cap; total_count is the whole match.
        params['limit'] = DISPUTES_PAGE_SIZE;
        params['offset'] = store.getters.CURRENT_PAGE * DISPUTES_PAGE_SIZE;
    }

    // Отключаем фильтры и теги для множественных Asset Type
    if (isOneAssetType === true) return params;

    // Filters
    const filter = store.getters.SELECT_FILTER;
    if (filter.length > 0) {
        let filtersID = [];
        let noneFiltersID = [];

        filter.forEach((filterValue, filterIndex) => {
            const filterData = filterValue.split('_');
            if (filterData[0] === 'none')
                noneFiltersID.push(Number(filterData[1]));
            else if (filterData[0] === 'checkbox')
                filtersID.push(Number(filterData[1]));
        });

        if (filtersID.length > 0) params['filters_id'] = filtersID;
        if (noneFiltersID.length > 0)
            params['not_in_filters_id'] = noneFiltersID;
    }

    // Tags
    const tag = store.getters.SELECT_TAG;
    if (tag.length > 0) params['tags_id'] = tag;

    return params;
}

export async function releaseAndUnreleaseFile(
    assetTypeID,
    releaseList = [],
    unreleaseList = []
) {
    const response = await axiosRequest({
        url: constants.URL.FILE_RELEASE,
        method: METHOD.POST,
        requestName: 'RELEASE FILE',
        data: {
            release: releaseList,
            unrelease: unreleaseList,
        },
        params: {
            asset_type_id: assetTypeID,
        },
    });

    return response == null ? false : true;
}

// Release every unreleased file of a whole provider in one server-side call.
// The backend reuses the per-file release path (index + count refresh), so we
// only pass provider + asset type — no client-side file-id gathering (which
// would trip the admin listing's 700-image cap on large providers).
// gameId narrows it to one game (the per-game menu on the main page).
export async function releaseAllProvider(providerId, assetTypeID, gameId = null) {
    const response = await axiosRequest({
        url: constants.URL.FILE_RELEASE_ALL,
        method: METHOD.POST,
        requestName: 'RELEASE ALL PROVIDER',
        params: {
            provider_id: providerId,
            asset_type_id: assetTypeID,
            ...(gameId == null ? {} : { game_name_id: gameId }),
        },
    });

    return response == null ? false : response.released;
}

export async function markFile(assetTypeID, file) {
    const response = await axiosRequest({
        url: constants.URL.FILE_MARK,
        method: METHOD.PATCH,
        requestName: 'MARK FILE',
        params: {
            asset_type_id: assetTypeID,
        },
        data: [
            {
                id: file.id,
                additional_status: file.additionalStatus,
                admin_tip: file.adminTip,
            },
        ],
    });

    return response == null ? false : true;
}

export async function recoveryFile(assetTypeID, fileID) {
    const response = await axiosRequest({
        url: constants.URL.FILE_RECOVERY,
        method: METHOD.POST,
        requestName: 'RECOVERY FILE',
        params: {
            asset_type_id: assetTypeID,
            files_id: fileID,
        },
    });

    return response == null ? false : true;
}

export async function deleteFile(assetTypeID, fileID) {
    const response = await axiosRequest({
        url: constants.URL.FILE_DELETE,
        method: METHOD.DELETE,
        requestName: 'DELETE FILE',
        params: {
            asset_type_id: assetTypeID,
            files_id: fileID,
        },
    });

    return response == null ? false : true;
}

// Erase for good (rows + S3) and tell the portal never to upload the file
// again. Not the deactivate-then-delete bin: there is nothing to recover.
export async function excludeFile(assetTypeID, providerID, fileID) {
    const response = await axiosRequest({
        url: constants.URL.FILE_EXCLUDE,
        method: METHOD.DELETE,
        requestName: 'ERASE FILE',
        params: {
            asset_type_id: assetTypeID,
            provider_id: providerID,
            files_id: fileID,
        },
    });

    return response == null ? null : response;
}

// One verdict for many disputed rows. `body.ids` names rows; without ids the
// backend takes every open dispute of `game_name_id` / `model_asset_type_id`,
// pages or not, so "the whole game" never pulls the list to the client.
export async function resolveDisputes(assetTypeID, body) {
    const response = await axiosRequest({
        url: constants.URL.FILE_DISPUTES_RESOLVE,
        method: METHOD.POST,
        requestName: 'RESOLVE DISPUTES',
        data: body,
        params: { asset_type_id: assetTypeID },
    });
    return response == null ? null : response;
}

// The sidebar badge: how many disputes wait. A number, not a page, so the
// listing cap that once read 1 293 disputes as "none" cannot hide them.
// No asset type: the backend assumes images, and the main page polls this
// before asset types are loaded.
export async function getDisputesCount() {
    const response = await axiosRequest({
        url: constants.URL.FILE_DISPUTES_COUNT,
        method: METHOD.GET,
        requestName: 'GET DISPUTES COUNT',
    });
    return response == null ? null : response.count;
}

export async function getFullImages(fileUuid) {
    const response = await axiosRequest({
        url: constants.URL.FILE_FULL_IMAGE,
        method: METHOD.GET,
        requestName: 'GET FULL SIZE IMAGE',
        params: {
            file_uuid: fileUuid,
        },
        isLoader: false,
    });

    return response;
}

// Open disputes per provider and game: [{id, name, count, games: [{id, name, count}]}]
// for the GUP uploads filter (869fg2gvw). null on failure: the main page's
// conflict filter must not read a failed request as "no conflicts" (869fheb1h).
export async function getDisputesSummary() {
    const response = await axiosRequest({
        url: constants.URL.FILE_DISPUTES_SUMMARY,
        method: METHOD.GET,
        requestName: 'GET DISPUTES SUMMARY',
        isLoader: false,
    });
    return response ?? null;
}
